import { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard, Users, Calendar, Stethoscope, Phone, Smile,
  Receipt, BarChart3, Settings, LogOut, ChevronRight, X, Lock,
  Workflow, PanelLeftClose, PanelLeftOpen, MessageCircle, ShieldCheck, Wallet, CircleDollarSign, PackageOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { CLINIC_LOGO_UPDATED } from "@/lib/clinicLogoEvents";
import { FeatureGate, useFeatures } from "@/context/FeaturesContext";

// Event the Checkout page listens for. When fired during an active checkout,
// the page's exit-gate dialog opens with the requested destination remembered
// (Save/Discard then navigates there). When the page isn't dirty, the
// listener just navigates directly. See Checkout.jsx for the receiver.
export const CHECKOUT_NAV_REQUEST = "dental-os:checkout-nav-request";
export const CHECKOUT_FOCUS_STATE = "dental-os:checkout-focus-state";

const NAV_SECTIONS = [
  {
    label: "Front office",
    items: [
      { to: "/dashboard",      label: "Dashboard",    Icon: LayoutDashboard },
      { to: "/calendar",       label: "Calendar",     Icon: Calendar },
      { to: "/patients",       label: "Patients",     Icon: Users },
      { to: "/recall-center",  label: "Recall",      Icon: Phone },
    ],
  },
  {
    label: "Doctor",
    items: [
      { to: "/doctor",         label: "Chairside",       Icon: Stethoscope, moduleKey: "chairside" },
      { to: "/tx-plans",       label: "Treatment plans", Icon: Workflow, feature: "tx_plan_center", moduleKey: "treatment_plans" },
      { to: "/ortho-center",   label: "Ortho",           Icon: Smile, feature: "ortho_module", moduleKey: "ortho" },
    ],
  },
  {
    label: "Billing",
    items: [
      { to: "/checkout",       label: "Checkout",         Icon: Receipt, moduleKey: "checkout" },
      { to: "/ar-center",      label: "Accounts Receivable", Icon: CircleDollarSign, moduleKey: "accounts_receivable" },
      { to: "/insurance-claims", label: "HMO Claims", Icon: ShieldCheck, moduleKey: "hmo_claims" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/inventory",      label: "Inventory",        Icon: PackageOpen, moduleKey: "inventory" },
      { to: "/reports",        label: "Reports",          Icon: BarChart3, anyOf: ["reports", "clinic_health_calculator"], moduleKey: "reports" },
      { to: "/expenses",       label: "Expenses",         Icon: Wallet, feature: "expenses", moduleKey: "expenses" },
    ],
  },
];

const SETTINGS_NAV = { to: "/settings", label: "Settings", Icon: Settings, match: "/settings" };

function navTestId(to) {
  return `nav-${to.replace(/\//g, "-").slice(1)}`;
}

export default function Sidebar({ open = false, collapsed = false, onClose, onToggleCollapse }) {
  const { activeClinic, signOut } = useAuth();
  const { access, loading: featuresLoading, plan, packageSegment, moduleLoading, canAccessModule } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [clinicLogoUrl, setClinicLogoUrl] = useState("");
  const [logoFailed, setLogoFailed] = useState(false);
  const [checkoutFocusActive, setCheckoutFocusActive] = useState(true);
  const checkoutAppointmentId = params.get("appointment_id");

  useEffect(() => {
    setCheckoutFocusActive(true);
    const handleFocusState = (event) => {
      setCheckoutFocusActive(event?.detail?.active !== false);
    };
    window.addEventListener(CHECKOUT_FOCUS_STATE, handleFocusState);
    return () => window.removeEventListener(CHECKOUT_FOCUS_STATE, handleFocusState);
  }, [location.pathname, checkoutAppointmentId]);

  const loadClinicLogo = useCallback(async () => {
    if (!activeClinic?.clinic_id) {
      setClinicLogoUrl("");
      setLogoFailed(false);
      return;
    }
    const { data, error } = await supabase.rpc("get_clinic_settings");
    if (error) return;
    setClinicLogoUrl(data?.logo_url || "");
    setLogoFailed(false);
  }, [activeClinic?.clinic_id]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!activeClinic?.clinic_id) {
        if (!cancelled) {
          setClinicLogoUrl("");
          setLogoFailed(false);
        }
        return;
      }
      const { data, error } = await supabase.rpc("get_clinic_settings");
      if (cancelled || error) return;
      setClinicLogoUrl(data?.logo_url || "");
      setLogoFailed(false);
    }
    run();
    return () => { cancelled = true; };
  }, [activeClinic?.clinic_id]);

  useEffect(() => {
    window.addEventListener(CLINIC_LOGO_UPDATED, loadClinicLogo);
    return () => window.removeEventListener(CLINIC_LOGO_UPDATED, loadClinicLogo);
  }, [loadClinicLogo]);

  const showClinicLogo = !!clinicLogoUrl && !logoFailed;
  // "Focus mode": when the receptionist is mid-checkout (active appointment
  // open), the sidebar nav items soft-disable so a stray click on Patients /
  // Calendar / Settings / etc. doesn't yank them out of the in-flight visit.
  // The deliberate exit path is the gated "Back to Calendar" button inside
  // the checkout itself. Logout still works for shift changes.
  const inActiveCheckout =
    location.pathname === "/checkout" && !!checkoutAppointmentId && checkoutFocusActive;
  const supportVisible = !moduleLoading && canAccessModule("support");
  const planPillLabel = featuresLoading ? null : sidebarPlanLabel(access, plan, packageSegment);

  const renderNavItem = ({ to, label, Icon, match, feature, anyOf, moduleKey }) => {
    if (moduleKey && (moduleLoading || !canAccessModule(moduleKey))) return null;
    // In focus mode the nav items are visually dimmed and clicking any
    // of them dispatches a CHECKOUT_NAV_REQUEST event. Checkout.jsx
    // listens, opens the exit dialog if there's staged work (Stay /
    // Save & leave / Discard everything), and routes the navigation
    // through the chosen action so the receptionist can step away
    // intentionally without losing the draft. If nothing's staged, the
    // listener just navigates directly -- no nag.
    if (inActiveCheckout) {
      const navItem = (
        <button
          key={to}
          type="button"
          data-testid={navTestId(to)}
          data-tutorial-id={navTestId(to)}
          onClick={() => {
            window.dispatchEvent(new CustomEvent(CHECKOUT_NAV_REQUEST, { detail: { to } }));
            if (window.innerWidth < 1024) onClose?.();
          }}
          title="You'll be asked to save or discard the checkout first"
          className={[
            "w-full text-left group flex items-center gap-3 px-3 py-3 rounded-lg text-[14px] text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors",
            collapsed ? "lg:justify-center lg:px-0" : "justify-between",
          ].join(" ")}
        >
          <span className={["flex items-center gap-3", collapsed ? "lg:justify-center" : ""].join(" ")}>
            <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
            <span className={collapsed ? "lg:hidden" : ""}>{label}</span>
          </span>
          <Lock className={["w-3.5 h-3.5 text-amber-400/70", collapsed ? "lg:hidden" : ""].join(" ")} strokeWidth={2.5} />
        </button>
      );
      return feature || anyOf ? <FeatureGate key={to} feature={feature} anyOf={anyOf}>{navItem}</FeatureGate> : navItem;
    }
    const navItem = (
      <NavLink
        key={to}
        to={to}
        end={!match}
        data-testid={navTestId(to)}
        data-tutorial-id={navTestId(to)}
        onClick={() => { if (window.innerWidth < 1024) onClose?.(); }}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          [
            "group flex items-center gap-3 px-3 py-3 rounded-lg text-[14px] transition-colors",
            collapsed ? "lg:justify-center lg:px-0" : "justify-between",
            isActive
              ? "bg-teal-600/15 text-teal-300"
              : "text-slate-300 hover:bg-white/5 hover:text-white",
          ].join(" ")
        }
      >
        {({ isActive }) => (
          <>
            <span className={["flex items-center gap-3", collapsed ? "lg:justify-center" : ""].join(" ")}>
              <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
              <span className={collapsed ? "lg:hidden" : ""}>{label}</span>
            </span>
            {isActive && (
              <ChevronRight className={["w-4 h-4 text-teal-300", collapsed ? "lg:hidden" : ""].join(" ")} strokeWidth={2.5} />
            )}
          </>
        )}
      </NavLink>
    );
    return feature || anyOf ? <FeatureGate key={to} feature={feature} anyOf={anyOf}>{navItem}</FeatureGate> : navItem;
  };

  const supportBottomLink = inActiveCheckout ? (
    <button
      type="button"
      data-testid="nav-support"
      data-tutorial-id="nav-support"
      onClick={() => {
        window.dispatchEvent(new CustomEvent(CHECKOUT_NAV_REQUEST, { detail: { to: "/support" } }));
        if (window.innerWidth < 1024) onClose?.();
      }}
      title="You'll be asked to save or discard the checkout first"
      className={[
        "w-full text-left group flex items-center gap-3 rounded-lg text-[14px] text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors",
        collapsed ? "lg:justify-center lg:px-0 px-3 py-3" : "justify-between px-3 py-3",
      ].join(" ")}
    >
      <span className={["flex items-center gap-3", collapsed ? "lg:justify-center" : ""].join(" ")}>
        <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2} />
        <span className={collapsed ? "lg:hidden" : ""}>Support</span>
      </span>
      <Lock className={["w-3.5 h-3.5 text-amber-400/70", collapsed ? "lg:hidden" : ""].join(" ")} strokeWidth={2.5} />
    </button>
  ) : (
    <NavLink
      to="/support"
      data-testid="nav-support"
      data-tutorial-id="nav-support"
      onClick={() => { if (window.innerWidth < 1024) onClose?.(); }}
      title={collapsed ? "Support" : undefined}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 rounded-lg text-[14px] transition-colors",
          collapsed ? "lg:justify-center lg:px-0 px-3 py-3" : "justify-between px-3 py-3",
          isActive
            ? "bg-teal-600/15 text-teal-300"
            : "text-slate-300 hover:bg-white/5 hover:text-white",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <span className={["flex items-center gap-3", collapsed ? "lg:justify-center" : ""].join(" ")}>
            <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2} />
            <span className={collapsed ? "lg:hidden" : ""}>Support</span>
          </span>
          {isActive && (
            <ChevronRight className={["w-4 h-4 text-teal-300", collapsed ? "lg:hidden" : ""].join(" ")} strokeWidth={2.5} />
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <aside
      data-testid="app-sidebar"
      className={[
        "fixed inset-y-0 left-0 z-40 bg-[#0F172A] text-white flex flex-col",
        "w-60",
        collapsed ? "lg:w-16" : "",
        "transform transition-[transform,width] duration-200 ease-in-out",
        open ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0",
      ].join(" ")}
    >
      {/* Brand + mobile close */}
      <div
        className={[
          "pt-6 pb-8 flex items-center gap-3",
          collapsed ? "px-3 lg:justify-center" : "px-5",
        ].join(" ")}
        data-testid="sidebar-brand"
      >
        <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center font-bold text-lg overflow-hidden shrink-0">
          {showClinicLogo ? (
            <img
              src={clinicLogoUrl}
              alt={`${activeClinic?.clinic_name ?? "Clinic"} logo`}
              data-testid="sidebar-clinic-logo"
              className="w-full h-full object-contain bg-white"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <span data-testid="sidebar-clinic-fallback">D</span>
          )}
        </div>
        <div className={["leading-tight min-w-0 flex-1", collapsed ? "lg:hidden" : ""].join(" ")}>
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0 truncate text-[15px] font-semibold">{activeClinic?.clinic_name ?? "Dental OS"}</div>
            {planPillLabel ? (
              <span
                data-testid="sidebar-plan-pill"
                className="shrink-0 rounded-full border border-teal-400/30 bg-teal-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-200"
              >
                {planPillLabel}
              </span>
            ) : null}
          </div>
          <div className="text-[11px] text-slate-400 truncate">{activeClinic?.slug ? `${activeClinic.slug}@dental-os.app` : "Clinic Platform"}</div>
        </div>
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-collapse-toggle"
          className={["hidden lg:inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-300 hover:bg-white/5 hover:text-white", collapsed ? "absolute left-11 top-7 bg-slate-900 shadow" : ""].join(" ")}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" strokeWidth={2.25} /> : <PanelLeftClose className="h-4 w-4" strokeWidth={2.25} />}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="lg:hidden -mr-1 p-2 rounded-md text-slate-300 hover:bg-white/5 hover:text-white"
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-5 overflow-y-auto">
        {inActiveCheckout ? (
          <div
            data-testid="sidebar-checkout-lock"
            className={["mx-1 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200 flex items-start gap-2", collapsed ? "lg:hidden" : ""].join(" ")}
          >
            <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.5} />
            <span>
              You&rsquo;re in the middle of a checkout. Click any link &mdash; we&rsquo;ll ask whether to save the draft or discard it before leaving.
            </span>
          </div>
        ) : null}
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} data-testid={`nav-section-${section.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={["px-3 pb-1 text-[11px] font-semibold text-slate-500", collapsed ? "lg:hidden" : ""].join(" ")}>
              {section.label}
            </div>
            <div className="space-y-1">
              {section.items.map(renderNavItem)}
            </div>
          </div>
        ))}
      </nav>

      <div className="m-3 space-y-1">
        {supportVisible ? supportBottomLink : null}
        <button
          data-testid="sidebar-logout"
          onClick={async () => { await signOut(); navigate("/login", { replace: true }); }}
          title={collapsed ? "Logout" : undefined}
          className={[
            "flex w-full items-center gap-3 rounded-lg text-[14px] text-slate-300 hover:bg-white/5 hover:text-white transition-colors",
            collapsed ? "lg:justify-center lg:px-0 px-3 py-3" : "px-3 py-3",
          ].join(" ")}
          type="button"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={2} />
          <span className={collapsed ? "lg:hidden" : ""}>Logout</span>
        </button>
        <div className="pt-3 mt-3 border-t border-white/10">
          {renderNavItem(SETTINGS_NAV)}
        </div>
      </div>
    </aside>
  );
}

function sidebarPlanLabel(access, plan, packageSegment) {
  const rawPlan = access?.plan || access?.plan_code || plan || packageSegment;
  const normalized = String(rawPlan || "").trim().toLowerCase();
  if (normalized === "solo") return "Solo";
  if (normalized === "clinic") return "Clinic";
  return null;
}
